import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  TableCell,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { TableVirtuoso } from "react-virtuoso";

import { AuthContext } from "../../components/auth-provider";
import { EnhancedTableHeadContents } from "../../components/enhanced-table";
import { TitledPage } from "../../components/titled-page";
import { VirtuosoTableComponents } from "../../components/enhanced-virtuoso-table";
import { loadApplicants } from "../../data-functions/applicants-api";
import { loadRecentSecurityCodes } from "../../data-functions/security-codes-api";
import { Applicant } from "../../types/applicants";
import { HeadCells, Order } from "../../types/tables";
import { formatDateToLocaleDateTime } from "../../utils/utils";

type SecurityCodeRow = {
  id: string;
  applicantName: string;
  arrivedAt: string;
  arrivedAtTimestamp: number;
  emailExtensionCode: string;
  securityCode: string;
};

const headCells: HeadCells = [
  {
    id: "applicantName",
    numeric: false,
    disablePadding: false,
    label: "Applicant",
    sortable: true,
    width: "30%",
  },
  {
    id: "emailExtensionCode",
    numeric: false,
    disablePadding: false,
    label: "Email extension code",
    sortable: true,
    width: "20%",
  },
  {
    id: "securityCode",
    numeric: false,
    disablePadding: false,
    label: "Security code",
    sortable: true,
    width: "20%",
  },
  {
    id: "arrivedAt",
    numeric: false,
    disablePadding: false,
    label: "Arrived at",
    sortable: true,
    width: "30%",
  },
];

const getApplicantName = (applicant: Applicant | undefined) => {
  if (!applicant) {
    return "Unknown applicant";
  }

  return `${applicant.name || ""} ${applicant.surname || ""}`.trim();
};

const normalizeSearchValue = (value: string) => value.trim().toLowerCase();

const SecurityCodesPage = () => {
  const { currentUser } = useContext(AuthContext)!;
  const [rows, setRows] = useState<SecurityCodeRow[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<string>("arrivedAt");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>("");

  const fetchSecurityCodes = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [securityCodes, applicants] = await Promise.all([
        loadRecentSecurityCodes(20),
        loadApplicants(currentUser?.appUser),
      ]);

      const applicantsByEmailExtensionCode = Object.values(applicants).reduce(
        (accumulator, applicant) => {
          const code = applicant.email_extension_code?.trim().toLowerCase();

          if (code) {
            accumulator[code] = applicant;
          }

          return accumulator;
        },
        {} as Record<string, Applicant>,
      );

      const isSponsorScopedUser = Boolean(currentUser?.appUser?.sponsorId);
      const nextRows = securityCodes
        .filter((securityCode) => securityCode.codeType === "email")
        .map((securityCode) => {
          const emailExtensionCode = securityCode.workerCode.trim();
          const applicant =
            applicantsByEmailExtensionCode[emailExtensionCode.toLowerCase()];

          if (isSponsorScopedUser && !applicant) {
            return null;
          }

          const arrivedAtDate = securityCode.createdAt?.toDate();

          if (!arrivedAtDate) {
            return null;
          }

          return {
            id: securityCode.id,
            applicantName: getApplicantName(applicant),
            arrivedAt: formatDateToLocaleDateTime(arrivedAtDate),
            arrivedAtTimestamp: arrivedAtDate.getTime(),
            emailExtensionCode,
            securityCode: securityCode.securityCode,
          };
        })
        .filter((row): row is SecurityCodeRow => !!row);

      setRows(nextRows);
      setLastUpdatedAt(formatDateToLocaleDateTime(new Date()));
    } catch (loadError) {
      console.error("Failed to load UK Visa security codes", loadError);
      setError("Failed to load security codes.");
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchSecurityCodes();
  }, [fetchSecurityCodes]);

  useEffect(() => {
    const refreshInterval = window.setInterval(() => {
      fetchSecurityCodes();
    }, 60_000);

    return () => {
      window.clearInterval(refreshInterval);
    };
  }, [fetchSecurityCodes]);

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visibleRows = useMemo(() => {
    const normalizedSearchValue = normalizeSearchValue(searchValue);
    const filteredRows = rows.filter((row) => {
      if (!normalizedSearchValue) {
        return true;
      }

      return [
        row.applicantName,
        row.emailExtensionCode,
        row.securityCode,
        row.arrivedAt,
      ].some((value) => value.toLowerCase().includes(normalizedSearchValue));
    });

    return filteredRows.slice().sort((firstRow, secondRow) => {
      let comparison = 0;

      if (orderBy === "arrivedAt") {
        comparison = firstRow.arrivedAtTimestamp - secondRow.arrivedAtTimestamp;
      } else {
        const leftValue =
          (firstRow[orderBy as keyof SecurityCodeRow] as string | number)
            ?.toString()
            .toLowerCase() || "";
        const rightValue =
          (secondRow[orderBy as keyof SecurityCodeRow] as string | number)
            ?.toString()
            .toLowerCase() || "";

        comparison = leftValue.localeCompare(rightValue);
      }

      return order === "asc" ? comparison : -comparison;
    });
  }, [order, orderBy, rows, searchValue]);

  return (
    <TitledPage title="UK Visa security codes">
      <Box>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 2,
            marginBottom: 2,
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search applicant, extension code, security code, or time"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            sx={{ minWidth: "360px" }}
          />
          <Tooltip title="Refresh security codes">
            <span>
              <IconButton color="primary" onClick={fetchSecurityCodes} disabled={isLoading}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            Showing {visibleRows.length} code{visibleRows.length === 1 ? "" : "s"} from the last
            20 minutes
            {lastUpdatedAt ? ` · Updated ${lastUpdatedAt}` : ""}
          </Typography>
        </Box>

        {isLoading && <LinearProgress sx={{ marginBottom: 2 }} />}

        {error && (
          <Typography color="error" sx={{ marginBottom: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ height: "calc(100vh - 240px)", minHeight: "400px" }}>
          <TableVirtuoso
            style={{ height: "100%" }}
            data={visibleRows.map((row) => [row.id, row] as [string, SecurityCodeRow])}
            context={{ selectedId: "", handleSelect: () => {} }}
            components={VirtuosoTableComponents}
            fixedHeaderContent={() => (
              <EnhancedTableHeadContents
                numSelected={0}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={() => {}}
                onRequestSort={handleRequestSort}
                rowCount={visibleRows.length}
                selectable={false}
                headCells={headCells}
              />
            )}
            itemContent={(_index, [_rowId, row]) => (
              <>
                <TableCell size="small">{row.applicantName}</TableCell>
                <TableCell size="small">{row.emailExtensionCode}</TableCell>
                <TableCell size="small">{row.securityCode}</TableCell>
                <TableCell size="small">{row.arrivedAt}</TableCell>
              </>
            )}
          />
        </Box>
      </Box>
    </TitledPage>
  );
};

export default SecurityCodesPage;
